// A screen's frame: the header — Portal's (avatar, wordmark) on account screens, the world header (resource strip, Reports ·
// Mail · city · Announcements · menu) inside a world — the bottom nav, the menu drawer, the world sheet and the
// announcements sheet.
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnnouncementsSheet } from "./AnnouncementsSheet";
import { useBack } from "./useBack";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { Village } from "@/api/types";
import { BottomNav, MenuDrawer, Shell, TitleHeader, WorldHeader, type MenuBadges, type NavBadges } from "@/kit/chrome";
import { Sheet } from "@/kit/overlays";
import { Button, Empty, Medallion, Row, RowText, cx } from "@/kit/primitives";
import { art } from "@/kit/art";
import { IconCrossedSwords, IconGlobe } from "@/kit/icons";
import { useSession, worldShort } from "@/state/session";
import { ownVillages, useInit } from "@/state/queries";
import { usePrefs } from "@/state/prefs";
import { badgeCount, otherWorldsIncoming, totalIncoming, worldAttack } from "@/game/incoming";
import { fmtNum } from "@/game/format";
import { useWorldIncoming } from "@/state/worldIncoming";
import { usePrompts } from "@/state/prompts";

export type HeaderSpec =
  | { kind: "title"; title: string; subtitle: ReactNode; onSubtitle?: () => void; subtitleLabel?: string }
  | { kind: "city"; city: string; meta: ReactNode; onCity?: () => void; onCityName?: () => void; /** the current city: the resource strip */ v?: Village;
      /** false: the menu bar without the resource strip (Store) */ strip?: boolean }
  /** no app header: the screen's own title bar is its header (Store — Rev. 11 §7: title, back and balance; no world/city/resource HUD) */
  | { kind: "bare" };

/** How a screen uses a tablet's or a PC's room (src/kit/responsive.css; on a portrait phone every kind is one column):
 *  grid — its cards flow into columns; col — one centred column (lists, forms); split — list | open item (Reports,
 *  Mail); full — edge to edge inside the rail (the map, the city view). */
/** The resource strip's taps (Ben, Oct 14 2026): wood, clay, iron, storage and population open their building in the city
 *  the strip shows — the header's own city first, since the session's vid can still be unset on a first visit. */
const stripCity = (w: string | null, vid: number | null | undefined, navigate: (to: string) => void) =>
  w && vid ? (k: string) => navigate(`${w}/city/${vid}/b/${k}`) : undefined;

export type PageLayout = "grid" | "col" | "split" | "full";

export function Page({ header, badges, menuBadges, notifications, avatarLevel, nav = true, layout = "grid", children }: {
  header: HeaderSpec;
  badges?: NavBadges;
  /** village/get's new_report / new_mail flags: the dots on the header's Reports and Mail */
  menuBadges?: MenuBadges;
  notifications?: number;
  avatarLevel?: ReactNode;
  nav?: boolean;
  layout?: PageLayout;
  children: ReactNode;
}) {
  const session = useSession();
  // every incoming attack across the player's cities, for the city box's ⚔ counter (Oct 14 2026)
  const hv = header.kind === "city" ? header.v : undefined;
  const incoming = useMemo(() => totalIncoming(ownVillages(hv, session.uid)), [hv, session.uid]);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [menu, setMenu] = useState(false);
  const [worlds, setWorlds] = useState(false);
  const [bell, setBell] = useState(false);
  const init = useInit();

  const signOut = useCallback(async () => {
    setMenu(false);
    try {
      await api.session.logout();
    } catch {
      /* the cookie is gone either way */
    }
    useSession.getState().setSignedOut();
    qc.clear();
    navigate("/sign-in", { replace: true });
  }, [qc, navigate]);

  // inside a world: Reports and Mail left of the city, Announcements right; on the Portal and account screens: the bell
  const { pathname } = useLocation();
  const inWorld = pathname.startsWith("/w/") && !!session.wid;
  // other worlds under attack (Ben, Oct 15 2026): the World tab glows red, the world sheet marks each one with its count.
  // The current world's total comes from the header's own city (its city box already shows it), not a second read.
  const current = inWorld ? session.wid : null;
  const headerWorld = inWorld && hv ? session.wid : null;
  const otherBook = useWorldIncoming(headerWorld);
  const attackBook = useMemo(() => {
    const m = new Map(otherBook);
    if (headerWorld !== null && hv) m.set(headerWorld, worldAttack(ownVillages(hv, session.uid)));
    return m;
  }, [otherBook, headerWorld, hv, session.uid]);
  const worldAlert = otherWorldsIncoming(attackBook, current);
  const worldsHit = [...attackBook.values()].filter((a) => a.incoming > 0).length;
  const common = {
    worldLabel: header.kind === "title" && header.title === "PORTAL" ? "All Worlds" : worldShort(session.wid),
    onWorld: () => setWorlds(true),
    worldAlert,
    notifications: notifications || undefined,
    onBell: () => setBell(true),
    onMenu: () => setMenu(true),
    avatarLevel,
    onAvatar: () => {
      if (session.wid && session.uid) navigate(`/w/${session.wid}/player/${session.uid}`);
      else usePrompts.getState().toast("Open a world to see your profile there.");
    },
  };
  const w = session.wid ? `/w/${session.wid}` : null
  // account screens other than the Portal (Settings, Worlds, Help) get a back button where the Portal has its avatar
  const back = useBack(w && session.vid ? `${w}/city/${session.vid}` : "/portal");
  const isPortal = header.kind === "title" && header.title === "PORTAL";

  const hdr =
    header.kind === "bare" ? undefined : header.kind === "title" ? (
      <TitleHeader {...common} title={header.title} subtitle={header.subtitle} onSubtitle={header.onSubtitle} subtitleLabel={header.subtitleLabel} onBack={isPortal ? undefined : back} />
    ) : (
      <WorldHeader {...common} city={header.city} meta={header.meta} onCity={header.onCity} v={header.v}
        onCityName={header.onCityName ?? (session.wid && session.vid ? () => navigate(`/w/${session.wid}/city/${session.vid}`) : undefined)}
        flags={{ reports: !!menuBadges?.reports, mail: !!menuBadges?.mail, news: !!notifications }}
        onReports={w ? () => navigate(`${w}/reports`) : undefined}
        onMail={w ? () => navigate(`${w}/mail`) : undefined} strip={header.strip !== false}
        incoming={incoming} onIncoming={() => { usePrefs.getState().set({ cityIncomingSort: true }); header.onCity?.(); }}
        onBuilding={stripCity(w, header.v?.village?.id ?? session.vid, navigate)} />
    );

  const worldList = useMemo(() => {
    const w = init.data?.world;
    const names = init.data?.config?.world_names ?? {};
    const inW = w?.worlds_in ?? [];
    const notIn = w?.worlds_not_in ?? [];
    const ended = w?.worlds_ended ?? [];
    return { inW, notIn, ended, names };
  }, [init.data]);

  return (
    <Shell className={cx(`pg-${layout}`, header.kind === "city" && "wf", header.kind === "city" && header.strip === false && "nostrip", header.kind === "bare" && "bare")} header={hdr} nav={nav ? <BottomNav wid={session.wid} badges={badges} /> : undefined}>
      {children}
      <MenuDrawer open={menu} onClose={() => setMenu(false)} login={session.login} email={session.email} wid={session.wid} uid={session.uid} onSignOut={signOut} />
      <Sheet open={worlds} onClose={() => setWorlds(false)} from="top" anchor=".whdr .wtab" label="Choose a world" title={<h3 className="eyebrow wl-head" style={{ margin: "0 0 14px" }}>Your worlds{worldsHit > 0 && <span className="wl-sum"><IconCrossedSwords />{worldsHit} {worldsHit === 1 ? "world" : "worlds"} under attack</span>}</h3>}>
        <div className="rows">
          <Row as="button" onClick={() => { setWorlds(false); navigate("/portal"); }} className={cx(!session.wid && "unread")}>
            <Medallion src={art.nav.portal} alt="" size={54} />
            <RowText line="All worlds" meta="Portal · account overview" />
          </Row>
          {worldList.inW.map((wid) => {
            const a = attackBook.get(wid);
            const n = a?.incoming ?? 0;
            const here = current === wid;
            return (
              <Row key={wid} as="button" alert={n > 0} onClick={() => { setWorlds(false); navigate(`/w/${wid}`); }} className={cx("wl-row", session.wid === wid && "unread", here && "here")}
                aria-label={n > 0 ? `World ${wid}${worldList.names[String(wid)] ? ` ${worldList.names[String(wid)]}` : ""}: ${n} incoming ${n === 1 ? "attack" : "attacks"}` : undefined}>
                <span className="cp-medal">
                  <Medallion size={54} glow={n > 0 ? "crim" : undefined}><IconGlobe /></Medallion>
                  {n > 0 && <span className="cp-medal-inc" aria-hidden="true"><IconCrossedSwords /></span>}
                </span>
                <div className="txt">
                  <div className="line"><span className="wl-name">World {wid}{worldList.names[String(wid)] ? <> · <b>{worldList.names[String(wid)]}</b></> : null}</span></div>
                  <div className="meta">{here ? "You're here" : "You play here"}</div>
                  {n > 0 && (
                    <div className="cp-incline">
                      <span className="cp-inc"><IconCrossedSwords /><b>{fmtNum(n)}</b> incoming · {a!.cities} {a!.cities === 1 ? "city" : "cities"}</span>
                    </div>
                  )}
                </div>
                {n > 0 && <span className="wl-cnt" aria-hidden="true"><IconCrossedSwords /><b>{badgeCount(n)}</b></span>}
              </Row>
            );
          })}
          {!worldList.inW.length && !init.isLoading && <Empty>You're not in any world yet.</Empty>}
        </div>
        <div style={{ marginTop: 16 }}>
          <Button kind="steel" size="md" wide onClick={() => { setWorlds(false); navigate("/worlds"); }}>
            All worlds · join a new one
          </Button>
        </div>
      </Sheet>
      <AnnouncementsSheet open={bell} onClose={() => setBell(false)} wid={inWorld ? session.wid : null} />
    </Shell>
  );
}

